import Navbar from "@/components/Navbar";
import { Link } from "react-router-dom";
import { Flame, Wallet, Swords, BadgeCheck, ShieldCheck, Scale } from "lucide-react";

const SECTIONS = [
  {
    icon: <Flame className="h-4 w-4 text-gold" />,
    title: "How ranking works",
    items: [
      "Every entity has one score: the total USD value backed by real people. No algorithms, no editorial picks — the score is the rank, in the open.",
      "Contributions are cumulative. Your total never decays: the only way past you is for someone to put in more.",
      "Backing a token or a KOL moves them up the board instantly. Overtakes are broadcast live as FLIPS in the activity feed.",
      "The exact amount needed to take the next rank is always public — use the Take #N widget on the leaderboard or the gap banner on any entity page.",
    ],
  },
  {
    icon: <Wallet className="h-4 w-4 text-gold" />,
    title: "Contributions",
    items: [
      "Back with the token itself, native coins (ETH, BNB, SOL), stablecoins (USDC, USDT), or BTC / DOGE / TRX — every contribution is valued in USD at the live price at confirmation time.",
      "Pay directly from your wallet, or send from an exchange with a payment request: each request carries a unique exact amount so your transfer can be matched automatically.",
      "In live mode every transaction is verified on-chain against the entity treasury before it counts. In demo mode contributions are simulated and clearly marked as such.",
      "Contributions are irreversible on-chain transfers. Never send funds you cannot afford to lock into your conviction.",
    ],
  },
  {
    icon: <Swords className="h-4 w-4 text-gold" />,
    title: "Listing tokens & KOLs",
    items: [
      "Tokens are listed by contract address on a supported network. New tokens start as 'coming soon' and go live on the board with their first real backing (≥ $1).",
      "KOLs are listed by X handle the same way: new callers start as 'coming soon' and enter the ranked board only after ≥ $1 of verified backing. Only the verified owner can edit the profile, photo and bio.",
      "Low-liquidity and honeypot contracts are rejected automatically — if the token can trap buyers, it cannot climb the board.",
    ],
  },
  {
    icon: <BadgeCheck className="h-4 w-4 text-gold" />,
    title: "Claims & flip alerts",
    items: [
      "KOL profiles can be claimed by posting a verification message from the official X account. Claimed profiles get a verified badge and management rights.",
      "Claimed profiles can register a webhook URL (Slack, Discord, Zapier…) and receive a POST the moment someone flips their rank. Getting flipped is a call to arms.",
    ],
  },
  {
    icon: <ShieldCheck className="h-4 w-4 text-gold" />,
    title: "Fair play",
    items: [
      "One wallet, one contribution, one count. The same transaction can never be counted twice.",
      "On-chain verification means claimed contributions that never happened are impossible: no hash, no rank.",
      "Entities that break the rules (fraudulent projects, scams, illegal content) are paused by moderation and lose their rank until resolved.",
      "Tracking parameters, affiliate links and URL shorteners are stripped from all public listings.",
    ],
  },
  {
    icon: <Scale className="h-4 w-4 text-gold" />,
    title: "The honest fine print",
    items: [
      "Crypto assets are volatile. Backing an entity is a bet on attention, not an investment product — FLIP is not financial advice and offers no returns.",
      "Ranks reflect money at risk, nothing more. A high rank means the community put serious funds behind it; it says nothing about future price.",
      "Demo mode exists so you can learn the game without moving real funds. Check the footer badge to know which mode you are in.",
    ],
  },
];

export default function RulesPage() {
  return (
    <div className="min-h-screen grain flex flex-col">
      <Navbar />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12">
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter sm:text-5xl">
          The rules of <span className="text-gold">the board.</span>
        </h1>
        <p className="mt-3 max-w-xl text-sm text-zinc-400">
          FLIP is a public leaderboard where rank is backed with real money and verified
          on-chain. Short rules, no fine print tricks.
        </p>

        <div className="mt-10 space-y-8" data-testid="rules-sections">
          {SECTIONS.map((s) => (
            <section key={s.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <h2 className="flex items-center gap-2 font-display text-lg font-black uppercase tracking-tight">
                {s.icon} {s.title}
              </h2>
              <ul className="mt-4 space-y-2.5">
                {s.items.map((item, i) => (
                  <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-zinc-300">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold/70" />
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-gold/25 bg-gold/[0.06] p-6 text-center">
          <p className="font-display text-lg font-black uppercase tracking-tight text-gold">
            Enough reading. Take the top.
          </p>
          <Link to="/" data-testid="rules-back-cta" className="mt-3 inline-block rounded-full bg-white px-6 py-2.5 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95">
            Back the board →
          </Link>
        </div>
      </main>
    </div>
  );
}
